'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { LIGHT_COLORS } from '@/constants';

import CuteFile from './CuteFile';

interface Props {
  type: 'primary' | 'secondary';
  fileLabel: string;
  fileColor: string;
  title: string;
  description: string;
  downloadUrl: string;
}

function FormatCard({
  type,
  fileLabel,
  fileColor,
  title,
  description,
  downloadUrl,
}: Props) {
  const [isHovered, setIsHovered] = React.useState(false);

  const Wrapper = type === 'primary' ? PrimaryCard : Card;

  return (
    <Wrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Background />
      <Foreground>
        <FileWrapper>
          <CuteFile
            isActive={isHovered}
            label={fileLabel}
            color={fileColor}
          />
        </FileWrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <DownloadLink download href={downloadUrl}>
          Download {fileLabel}
        </DownloadLink>
      </Foreground>
    </Wrapper>
  );
}

const Card = styled.article`
  flex: 1;
  position: relative;
  z-index: 1;
  margin-bottom: 16px;
`;

const Background = styled.div`
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: ${LIGHT_COLORS['--color-background']};
  box-shadow:
    0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    -1px 6.7px 5.3px rgba(0, 0, 0, 0.028),
    -2px 12.5px 10px rgba(0, 0, 0, 0.035),
    -5px 22.3px 17.9px rgba(0, 0, 0, 0.042),
    -10px 41.8px 33.4px rgba(0, 0, 0, 0.05),
    -20px 100px 80px rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  transition: transform 250ms;

  ${Card}:hover & {
    transform: scale(1.025);
  }
`;
const Foreground = styled.div`
  position: relative;
  z-index: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;
const PrimaryCard = styled(Card)`
  flex: 1.2;
`;

const FileWrapper = styled.div`
  margin-top: -42px;
  display: flex;
  justify-content: center;
  cursor: default;
  transition: transform 400ms cubic-bezier(0.17, 0.67, 0.63, 0.97);
  will-change: transform;

  ${Card}:hover & {
    transform: translateY(-10px) rotate(-20deg);
  }
`;

const Title = styled.h2`
  font-size: 1.3125rem;
  font-weight: var(--font-weight-medium);
  margin-top: 12px;
  margin-bottom: 12px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 0.9375rem;
  color: ${LIGHT_COLORS['--color-gray-700']};
  font-style: italic;
  text-align: center;
  margin-bottom: 24px;
  flex: 1;
`;

const DownloadLink = styled.a`
  display: block;
  text-decoration: none;
  padding: 8px;
  background: ${LIGHT_COLORS['--color-primary']};
  color: white;
  text-align: center;
  border-radius: 8px;
  width: 100%;

  &:focus {
    outline-offset: 4px;
    outline-color: black;
  }

  &:hover {
    background: hsl(260deg, 100%, 47%);
  }
`;

export default FormatCard;
